import React, { useEffect, useRef, useState } from 'react';

interface CopyLinkButtonProps {
  /** The address to copy, read at click time. */
  getUrl: () => string;
}

type CopyState = 'idle' | 'copied' | 'failed';

const RESET_MS = 2500;

/** Older browsers and non-secure origins have no Clipboard API: copy through a hidden textarea. */
function legacyCopy(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(area);
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission refused or not focused: fall through to the old way.
  }
  return legacyCopy(text);
}

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M6.5 9.5l3-3M5 11l-1 1a2.1 2.1 0 01-3-3l2.5-2.5a2.1 2.1 0 013 0M11 5l1-1a2.1 2.1 0 013 3l-2.5 2.5a2.1 2.1 0 01-3 0" />
  </svg>
);

/** "Copy link", which confirms with "Link copied" (or says it couldn't) for a moment. */
const CopyLinkButton: React.FC<CopyLinkButtonProps> = ({ getUrl }) => {
  const [state, setState] = useState<CopyState>('idle');
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const onClick = async () => {
    const ok = await copyText(getUrl());
    setState(ok ? 'copied' : 'failed');
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState('idle'), RESET_MS);
  };

  const label = state === 'copied' ? 'Link copied' : state === 'failed' ? "Couldn't copy" : 'Copy link';

  return (
    <button type="button" className={`pl-btn pl-btn-outline${state === 'copied' ? ' is-copied' : ''}`} onClick={onClick}>
      <LinkIcon />
      <span aria-live="polite">{label}</span>
    </button>
  );
};

export default CopyLinkButton;
